import { 
  Component, 
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
  ViewChild,
  Inject,
  ChangeDetectorRef
} from '@angular/core';

import { 
  FormGroup,
  FormControl,
  Validators 
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ViewerComponent } from '@grapecity/activereports-angular';

import { SharedModule } from './shared.module';
import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { Invoices_ShellService, EModalSize, EToasterType, EToasterPosition } from './Invoices.shell.service';
import { Invoices_OperationService } from './Invoices.operation.service';
import { Invoices_DatasourceService } from './Invoices.datasource.index';
import { Invoices_FlowService } from './Invoices.flow.index';
import { Invoices_ReportService } from './Invoices.report.index';
import { Invoices_LocalizationService } from './Invoices.localization.service';
import { Language } from './localization.service';
import { $types } from './Invoices.types'

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'Invoices-invoice_report',
  templateUrl: './Invoices.invoice_report.component.html'
})
export class Invoices_invoice_reportComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('reportViewer', { static: false }) reportViewer: ViewerComponent;

  report: any;
  reportParameters: any;

  inParams: { invoiceId: number } = { invoiceId: null };
  //#region Inputs
  @Input('invoiceId') set $inParams_invoiceId(v: number) {
    this.inParams.invoiceId = v;
  }
  get $inParams_invoiceId(): number {
    return this.inParams.invoiceId;
  }
  //#endregion Inputs

  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  @Output()
  $refreshEvent = new EventEmitter();
  //#endregion Outputs

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  //#endregion
  constructor(
    private utils: UtilsService,
    private settings: SettingsValuesService,
    private shell: Invoices_ShellService,
    private datasources: Invoices_DatasourceService,
    private flows: Invoices_FlowService,
    private reports: Invoices_ReportService,
    private localization: Invoices_LocalizationService,
    private operations: Invoices_OperationService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if(!this.$hasMissingRequiredInParams) {
      this.$init();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      }
    }
  }

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }
  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.invoiceId)) {
        this.$missingRequiredInParams.push('invoiceId');
      }
  }

  initialized = false;

  async $init() {
    this.title = 'Invoice Report';
    await this.$dataLoad();
    this.initialized = true;
  }

  async $dataLoad() {
    const $report = this;
    const $utils = this.utils;

    // We want to use reportViewer.open only in the onViewerInit, otherwise
    // the reportViewer might not be ready. In order to do that, the viewer has to be recreated.
    // We recreate it by using *ngIf="report"

    // set the report to null so that the reportViewer gets destroyed (initially this will change nothing)
    this.report = null;

    // actually destroy the viewer
    this.changeDetector.detectChanges();

    // set the report to the actual one which should create the viewer
    this.report = await this.reports.Invoices.invoice_report.getReport(this.inParams);
    this.reportParameters = this.reports.Invoices.invoice_report.getReportParameters(this.inParams);
  }

  onViewerInit() {
    this.reportViewer?.open(this.report, {
        ReportParams: this.reportParameters
      });
  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    if (this.$hasMissingRequiredInParams) {
      return Promise.resolve(null);
    }
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = this.$dataLoad();

    // children
    
    return result;
  }

  close() {
    this.$finish.emit();
  }
}
